/**
 * Connected Sources Strip Component
 * =================================
 * Premium glassmorphism strip showing connected integrations and data sources
 * Apple-inspired design with trust indicators
 * Now with industry-specific data sources and compliance badges
 */
import React, { useState, useEffect, useMemo } from 'react';
import { 
  ShieldCheck, 
  Database, 
  RefreshCw, 
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Zap,
  Scale,
  Building2,
  FileCheck,
  Info
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  GOVERNMENT_SOURCES, 
  getIndustryDataSources, 
  getComplianceSources 
} from '@/config/industryDataSources';
import { INTEGRATION_META } from './_integration_meta';
import { API_URL } from '@/config/apiConfig';

// Integration Logo Component
const IntegrationLogo = ({ integration, isConnected = true, size = 'md' }) => {
  const meta = INTEGRATION_META[integration?.toLowerCase()] || {};
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid={`source-badge-${(integration || '').toLowerCase()}`}
            className={`relative ${sizeClasses[size]} rounded-lg bg-white shadow-sm border border-slate-200/60 p-1.5 
            ${isConnected ? 'opacity-100' : 'opacity-40 grayscale'} 
            hover:scale-110 hover:shadow-md transition-all duration-200 cursor-pointer`}
          >
            {meta.logo && (
              <img 
                src={meta.logo} 
                alt={meta.name || integration} 
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            {/* Always-present name fallback: shows when there's no logo OR the
                logo image fails to load (e.g. blocked CDN), so the source is
                never an invisible empty box. */}
            <div
              className="w-full h-full items-center justify-center text-[10px] font-bold text-slate-500"
              style={{ display: meta.logo ? 'none' : 'flex' }}
            >
              {(meta.name || integration || '').slice(0, 2).toUpperCase()}
            </div>
            {isConnected && (
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                <CheckCircle2 className="w-2 h-2 text-white" />
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <p className="font-medium">{meta.name || integration}</p>
          <p className="text-slate-400">{isConnected ? 'Connected' : 'Not connected'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Government Source Badge with industry context
const GovernmentSourceBadge = ({ source, isCompliance = false }) => {
  const meta = typeof source === 'string' 
    ? GOVERNMENT_SOURCES[source?.toLowerCase()] 
    : source;
  
  if (!meta) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`
            flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium 
            hover:scale-105 transition-all cursor-pointer
            ${isCompliance 
              ? 'bg-amber-900 text-amber-100 hover:bg-amber-800' 
              : 'bg-slate-900 text-white hover:bg-slate-800'
            }
          `}>
            {isCompliance ? (
              <FileCheck className="w-3 h-3 text-amber-300" />
            ) : (
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
            )}
            <span>{meta.name || source}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs max-w-[250px]">
          <p className="font-semibold">{meta.fullName || source}</p>
          <p className="text-slate-400 mt-1">{meta.description}</p>
          <div className="flex items-center gap-1 mt-2 text-emerald-400">
            <ShieldCheck className="w-3 h-3" />
            <span>{meta.trustLevel} Data Source</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Compliance Section Badge
const ComplianceBadge = ({ sources = [], industry }) => {
  if (!sources || sources.length === 0) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg text-[11px] font-medium cursor-pointer hover:from-amber-500 hover:to-orange-500 transition-all">
            <Scale className="w-3.5 h-3.5" />
            <span>Compliance Active</span>
            <Badge className="bg-white/20 text-white text-[9px] px-1.5 py-0 ml-1">
              {sources.length}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs max-w-[280px]">
          <p className="font-semibold mb-2">Industry Compliance Monitoring</p>
          <div className="space-y-1.5">
            {sources.map((src, idx) => (
              <div key={`${src?.id ?? src?.name ?? src?.title ?? 'src'}-${idx}`} className="flex items-center gap-2 text-slate-300">
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                <span>{src.fullName || src.name}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ConnectedSourcesStrip = ({ 
  connectedIntegrations = [],
  governmentSources = ['ato', 'abs', 'rba'],
  industry = 'professional',
  lastSync = null,
  onManageIntegrations,
  compact = false,
  className = ''
}) => {
  const [isLive, setIsLive] = useState(true);

  // Get industry-specific data sources
  const industryData = useMemo(() => {
    return getIndustryDataSources(industry);
  }, [industry]);

  // Get compliance sources for this industry
  const complianceSources = useMemo(() => {
    return getComplianceSources(industry);
  }, [industry]);

  // Determine if data is fresh (synced within last hour)
  useEffect(() => {
    if (lastSync) {
      const syncTime = new Date(lastSync);
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
      setIsLive(syncTime > hourAgo);
    }
  }, [lastSync]);

  // Use industry-specific government sources, or fallback to provided ones
  const primarySources = industryData?.governmentSources?.length > 0 
    ? industryData.governmentSources.slice(0, 3)
    : governmentSources.map(id => GOVERNMENT_SOURCES[id]).filter(Boolean);

  // Strictly use the live connected integrations passed in. Never fall back to
  // industry-recommended or hardcoded sources — live accounts must only ever
  // show data sources they have actually connected.
  const integrations = Array.isArray(connectedIntegrations)
    ? connectedIntegrations.filter(Boolean)
    : [];
  const hasSources = integrations.length > 0;

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {hasSources ? (
          <div className="flex items-center gap-1">
            {integrations.slice(0, 3).map((int, idx) => (
              <IntegrationLogo key={`${int?.id ?? int?.name ?? int?.title ?? 'int'}-${idx}`} integration={int} size="sm" />
            ))}
            {integrations.length > 3 && (
              <span className="text-xs text-slate-400 ml-1">+{integrations.length - 3}</span>
            )}
          </div>
        ) : (
          <button
            type="button"
            data-testid="sources-connect-cta-compact"
            onClick={onManageIntegrations}
            className="text-xs text-[#0066CC] hover:underline"
          >
            Connect a data source
          </button>
        )}
        <div className="h-4 w-px bg-slate-200" />
        <Badge 
          variant="outline" 
          className={`text-[10px] ${isLive ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}
        >
          <div className={`w-1.5 h-1.5 rounded-full mr-1 ${isLive ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
          {isLive ? 'Live' : 'Syncing'}
        </Badge>
      </div>
    );
  }

  return (
    <div className={`
      bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-4
      shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)]
      ${className}
    `} data-testid="connected-sources-strip">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        {/* Left: Status and Connected Sources */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Live Status Indicator */}
          <div className="flex items-center gap-2">
            {hasSources ? (
              <div className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                ${isLive 
                  ? 'bg-gradient-to-r from-emerald-500 to-[#0066CC] text-white shadow-lg shadow-emerald-500/20' 
                  : 'bg-amber-100 text-amber-700'}
              `}>
                <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-white animate-pulse' : 'bg-amber-500'}`} />
                {isLive ? 'Data Live' : 'Syncing...'}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                <div className="w-2 h-2 rounded-full bg-slate-400" />
                No live data yet
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="h-8 w-px bg-slate-200 hidden sm:block" />

          {/* Connected Integrations */}
          <div className="flex items-center gap-2">
            {hasSources ? (
              <>
                <span className="text-xs text-slate-500 hidden sm:inline">Your data from:</span>
                <div className="flex items-center gap-1">
                  {integrations.slice(0, 4).map((int, idx) => (
                    <IntegrationLogo key={`${int?.id ?? int?.name ?? int?.title ?? 'int'}-${idx}`} integration={int} size="md" />
                  ))}
                  {integrations.length > 4 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 rounded-lg p-0 text-xs text-slate-500 hover:text-slate-900"
                      onClick={onManageIntegrations}
                    >
                      +{integrations.length - 4}
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <button
                type="button"
                data-testid="sources-connect-cta"
                onClick={onManageIntegrations}
                className="flex items-center gap-1.5 text-xs font-medium text-[#0066CC] hover:underline"
              >
                <Database className="w-3.5 h-3.5" />
                Connect a data source
              </button>
            )}
          </div>
        </div>

        {/* Right: Government Sources, Compliance & Action */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Industry-Specific Government Data Sources */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500 hidden lg:inline">Benchmarks:</span>
            {primarySources.slice(0, 3).map((source, idx) => (
              <GovernmentSourceBadge key={`${source?.id ?? source?.name ?? source?.title ?? 'source'}-${idx}`} source={source} />
            ))}
          </div>

          {/* Compliance Badge - Industry Specific */}
          {complianceSources.length > 0 && (
            <ComplianceBadge sources={complianceSources} industry={industry} />
          )}

          {/* Manage Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-slate-500 hover:text-slate-900"
            onClick={onManageIntegrations}
          >
            Manage
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Industry Context & Sync Info */}
      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-slate-400">
        <div className="flex items-center gap-3">
          {/* Industry Badge */}
          <div className="flex items-center gap-1.5">
            <Building2 className="w-3 h-3 text-indigo-400" />
            <span className="capitalize">{industry.replace('_', ' ')} Industry</span>
          </div>
          
          {/* Data Description */}
          {industryData?.description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-help">
                    <Info className="w-3 h-3" />
                    <span className="hidden sm:inline truncate max-w-[200px]">{industryData.description}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px]">
                  <p>{industryData.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {lastSync && (
            <div className="flex items-center gap-1.5">
              <RefreshCw className="w-3 h-3" />
              <span>Last sync: {new Date(lastSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-amber-500" />
            <span>{integrations.length} sources • {primarySources.length + complianceSources.length} authorities</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectedSourcesStrip;
